import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { RecordSheet } from '@ordo/ui/record-sheet'
import { type Column, DataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { type Audit, type AuditAction, type AuditSource, useAudit, useAudits } from 'hooks/api'
import { Bot, Globe, UserRound } from 'lucide-react'
import { useState } from 'react'

const SOURCE_OPTIONS: { value: AuditSource | ''; label: string }[] = [
  { value: '', label: 'All sources' },
  { value: 'MANUAL', label: 'User action' },
  { value: 'WEBHOOK', label: 'Webhook' },
  { value: 'SYSTEM', label: 'System' },
]

const ACTION_OPTIONS: { value: AuditAction | ''; label: string }[] = [
  { value: '', label: 'All actions' },
  { value: 'CREATED', label: 'Created' },
  { value: 'MODIFIED', label: 'Modified' },
  { value: 'DELETED', label: 'Deleted' },
  { value: 'OTHER', label: 'Other' },
]

/**
 * Admin-only audit log browser. Shows every recorded audit row across
 * three sources — UI clicks, incoming webhook activity, and background
 * processes — with filters by source/action and a detail sheet that
 * surfaces the structured metadata (sanitized webhook payload, changed
 * fields, bulk counts, etc.) per row.
 */
export function AdminAudits() {
  const [source, setSource] = useState<AuditSource | ''>('')
  const [action, setAction] = useState<AuditAction | ''>('')
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, loading } = useAudits({
    take: 100,
    ...(source ? { source } : {}),
    ...(action ? { action } : {}),
  })

  const columns: Column<Audit>[] = [
    {
      key: 'source',
      header: 'Source',
      mobile: 'aside',
      sortable: true,
      render: (row) => <SourceBadge source={row.source} />,
    },
    {
      key: 'action',
      header: 'Action',
      mobile: 'title',
      sortable: true,
      render: (row) => <ActionBadge action={row.action} />,
    },
    {
      key: 'user',
      header: 'Actor',
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.user.name ?? row.user.email}</span>
          {row.user.name ? <span className="text-xs text-muted-foreground">{row.user.email}</span> : null}
        </div>
      ),
    },
    {
      key: 'notes',
      header: 'Details',
      mobile: 'hidden',
      render: (row) => (
        <button type="button" className="text-left text-foreground hover:underline" onClick={() => setOpenId(row.id)}>
          {row.notes ?? '—'}
        </button>
      ),
    },
    {
      key: 'createdAt',
      header: 'When',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
  ]

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Audit log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every recorded action across the app: UI clicks, incoming webhooks, and background processes. Click a row to
            inspect the structured metadata.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Source"
          value={source}
          onChange={(v) => setSource(v as AuditSource | '')}
          options={SOURCE_OPTIONS}
        />
        <FilterSelect
          label="Action"
          value={action}
          onChange={(v) => setAction(v as AuditAction | '')}
          options={ACTION_OPTIONS}
        />
      </div>

      <DataGrid
        columns={columns}
        data={data?.rows ?? []}
        keyField="id"
        pageSize={20}
        loading={loading}
        storageKey="admin-audits"
      />

      <AuditDetailSheet id={openId} onClose={() => setOpenId(null)} />
    </Page>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border bg-background px-2 py-1 text-sm">
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function SourceBadge({ source }: { source: AuditSource }) {
  const icon = source === 'WEBHOOK' ? Globe : source === 'SYSTEM' ? Bot : UserRound
  const Icon = icon
  const label = source === 'WEBHOOK' ? 'Webhook' : source === 'SYSTEM' ? 'System' : 'User'
  return (
    <Badge variant="outline" className="gap-1">
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  )
}

function ActionBadge({ action }: { action: AuditAction }) {
  if (action === 'CREATED') {
    return <Badge variant="default">Created</Badge>
  }
  if (action === 'MODIFIED') {
    return <Badge variant="secondary">Modified</Badge>
  }
  if (action === 'DELETED') {
    return (
      <Badge variant="secondary" className="bg-red-500/15 text-red-600 dark:text-red-400">
        Deleted
      </Badge>
    )
  }
  return <Badge variant="outline">Other</Badge>
}

function AuditDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data: row } = useAudit(id)
  const open = id !== null

  const entityRows = row
    ? (
        [
          ['Contact', row.contactId],
          ['Contact group', row.contactGroupId],
          ['Template', row.templateId],
          ['Scheduled communication', row.scheduledCommunicationId],
          ['Schedule', row.scheduleId],
        ] as const
      ).filter(([, value]) => value !== null)
    : []

  return (
    <RecordSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
        }
      }}
      title={row?.notes ?? (row ? `${row.action} (${row.source})` : 'Audit')}
      description={row ? `${row.user.name ?? row.user.email} · ${new Date(row.createdAt).toLocaleString()}` : undefined}
      meta={
        row ? (
          <div className="flex flex-wrap items-center gap-2">
            <SourceBadge source={row.source} />
            <ActionBadge action={row.action} />
          </div>
        ) : null
      }
      size="xl"
      footerActions={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }>
      {row ? (
        <div className="space-y-6">
          {entityRows.length > 0 ? (
            <section>
              <h3 className="mb-2 text-sm font-medium">Related entities</h3>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                {entityRows.map(([label, value]) => (
                  <div key={label} className="contents">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="font-mono text-xs">{value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          {Object.keys(row.metadata).length > 0 ? (
            <section>
              <h3 className="mb-2 text-sm font-medium">Metadata</h3>
              <pre className="overflow-x-auto rounded-md border bg-muted p-3 text-xs">
                {JSON.stringify(row.metadata, null, 2)}
              </pre>
            </section>
          ) : (
            <p className="text-sm text-muted-foreground">No structured metadata recorded.</p>
          )}
        </div>
      ) : (
        <div className="py-12 text-center text-muted-foreground">Loading…</div>
      )}
    </RecordSheet>
  )
}
